/**
 * localStorage-backed personal bests. Key: `laniaamu.pb`.
 *
 * The stored value is a map of `categoryKey -> RunResult`, so each dose keeps
 * its own best. Values written by the single-best version of the game are
 * migrated to the default category on read.
 *
 * Every read is defensive: a corrupt or hand-edited value must never break the
 * game, it should just look like "no PB yet".
 */

import { REFERENCE_INTOXICATION, categoryKey, sanitiseLevel } from '../config/intoxication';
import type { RunResult, ScoreRepository } from './ScoreRepository';

export const PB_STORAGE_KEY = 'laniaamu.pb';

export function isValidRunResult(value: unknown): value is RunResult {
  if (!value || typeof value !== 'object') return false;
  const run = value as Partial<RunResult>;
  return (
    typeof run.timeMs === 'number' &&
    Number.isFinite(run.timeMs) &&
    run.timeMs > 0 &&
    Array.isArray(run.splits)
  );
}

/** Fills in the fields a pre-category save would not have had. */
function normalise(value: RunResult): RunResult {
  const intoxication = sanitiseLevel(value.intoxication ?? REFERENCE_INTOXICATION);
  return {
    ...value,
    intoxication,
    category: value.category ?? categoryKey(intoxication),
    hannuHits: typeof value.hannuHits === 'number' ? value.hannuHits : 0,
  };
}

type StoredMap = Record<string, RunResult>;

export class LocalStorageScoreRepository implements ScoreRepository {
  constructor(private readonly key = PB_STORAGE_KEY) {}

  async loadPersonalBest(category: string): Promise<RunResult | null> {
    return this.read()[category] ?? null;
  }

  async savePersonalBest(run: RunResult): Promise<void> {
    const all = this.read();
    all[run.category] = run;
    this.write(all);
  }

  async loadAll(): Promise<RunResult[]> {
    return Object.values(this.read()).sort((a, b) => a.timeMs - b.timeMs);
  }

  async clear(): Promise<void> {
    try {
      globalThis.localStorage?.removeItem(this.key);
    } catch {
      // ignore
    }
  }

  private read(): StoredMap {
    try {
      const raw = globalThis.localStorage?.getItem(this.key);
      if (!raw) return {};
      const parsed: unknown = JSON.parse(raw);

      // Legacy shape: a single RunResult from before categories existed.
      if (isValidRunResult(parsed)) {
        const migrated = normalise(parsed);
        return { [migrated.category]: migrated };
      }

      if (!parsed || typeof parsed !== 'object') return {};
      const result: StoredMap = {};
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (isValidRunResult(value)) result[key] = normalise(value);
      }
      return result;
    } catch {
      return {};
    }
  }

  private write(all: StoredMap): void {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(all));
    } catch {
      // Private browsing / quota - a lost PB is not worth crashing a run over.
    }
  }
}
